import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface SessionContextValue {
  session: Session | null;
  isLoaded: boolean;
  // Signs the user out and, on success, sends them to the homepage. Returns
  // any error so callers can surface it; they don't need to navigate.
  signOut: () => Promise<{ error: { message: string } | null }>;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

// Owns the single Supabase auth subscription for the whole app. Every
// `useSession()` reads from here, so there's one `getSession` + one
// `onAuthStateChange` listener instead of one per consuming component, and
// sign-out (including the redirect) lives in exactly one place.
export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setSession(data.session);
        setIsLoaded(true);
      })
      .catch((err) => {
        if (!mounted) return;
        // eslint-disable-next-line no-console
        console.error('Failed to read Supabase session', err);
        setIsLoaded(true);
      });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!mounted) return;
      setSession(nextSession);
      setIsLoaded(true);
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      // Navigate home *after* the session clears so it overwrites the
      // /sign-in redirect ProtectedRoute fires while briefly mounted
      // without a session.
      navigate('/', { replace: true });
    }
    return { error: error ?? null };
  }, [navigate]);

  const value = useMemo(
    () => ({ session, isLoaded, signOut }),
    [session, isLoaded, signOut]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};
